import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import Loading from '@/components/Loading/Loading';
import type { CustomerOrder } from '@/lib/smartForm/types';
import { getShortenCountryName } from '@/lib/use-origin-country';
import settings from '@/settings/configs';
import i18n from '@/settings/i18n';
import AccountNav from '@/components/accounts/AccountNav';
import OrderUnit from '@/components/accounts/OrderUnit';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import AccountRecommendations from '@/components/accounts/AccountRecommendations';
import { logger } from '@/lib/logger';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import ContactUsForm from '@/components/help/contact-us-form';
import { CtaButton } from '@/components/Cta/Cta';

const t = i18n.init();

export default function Orders() {
  const router = useRouter();
  const { customer } = useCustomerContext();
  const [nextPage, setNextPage] = useState<number>(1);
  const [orderData, setOrderData] = useState<CustomerOrder[] | undefined>(
    undefined,
  );
  const [orderError, setOrderError] = useState<Error | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const parentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!customer) router.push(`/account/login?redirectUrl=/account/orders`);
  }, [customer]);

  const getOrderData = async (url: string) => {
    if (isFetching) return { customerOrders: [], nextPage };
    let baseUrl = `${url}?email=${encodeURIComponent(customer?.email ?? '')}`;

    setIsFetching(true);

    if (nextPage) baseUrl += `&page=${nextPage}`;

    try {
      const response = await fetch(baseUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      const {
        customerOrders,
        nextPage: _nextPage,
        hasNextPage: _hasNextPage,
      } = await response.json();

      setNextPage(_nextPage);
      setHasNextPage(_hasNextPage);
      setOrderData(customerOrders);
      setIsFetching(false);

      return { customerOrders, nextPage } as {
        customerOrders: CustomerOrder[];
        nextPage: number;
      };
    } catch (error) {
      setOrderError(error as Error);
      setIsFetching(false);

      return { customerOrders: [], nextPage };
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    getOrderData('/api/accounts/orders');
  }, []);

  const endOfContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && nextPage && !isFetching && hasNextPage)
          getOrderData('/api/accounts/orders');
      },
      {
        root: null,
        rootMargin: '0px 0px 500px 0px', // Trigger 500px before the end
        threshold: 0, // Trigger as soon as the target is within the margin
      },
    );

    if (endOfContainerRef.current) observer.observe(endOfContainerRef.current);

    return () => {
      if (endOfContainerRef.current)
        observer.unobserve(endOfContainerRef.current);
    };
  }, [getOrderData]);

  if (orderError) logger.error({ orderError }, 'Order error');

  dayjs.extend(advancedFormat);

  const shouldRenderPage = orderData !== undefined && customer != null;

  return (
    <>
      <Head>
        <title>{t`Account Orders - Reebelo ${{
          country: getShortenCountryName(),
        }}`}</title>
        <WebsiteSchema />
        <meta
          name="description"
          content={t`Reebelo ${{
            country: settings.country,
          }} is APAC's leading marketplace for sustainable tech with offices in ${{
            country_office_locations: settings.country_office_locations,
          }}. The positive customer experience at Reebelo ${{
            country: settings.country,
          }} is of utmost importance to us. Please check here for Reebelo ${{
            country: settings.country,
          }} Account Orders.`}
        />
      </Head>
      <div className="bg-white">
        <div className="reebelo-container">
          <div className="pt-4">
            <p className="text-sm text-gray-700">
              <Link href="/">{t`Home`}</Link> / {t`My Orders`}
            </p>
          </div>
          <h1 className="mb-2 pt-2 text-base font-bold text-gray-700 lg:text-xl">
            {t`My Orders`}
          </h1>
          <div
            className="flex items-stretch gap-5 pb-14"
            ref={parentContainerRef}
          >
            <AccountNav />
            <div className="flex h-auto w-full flex-col lg:w-10/12">
              {!shouldRenderPage ? (
                <Loading />
              ) : (
                <>
                  {orderData !== undefined && orderData.length === 0 ? (
                    <div className="h-auto w-full rounded lg:w-8/12">
                      <div className="w-full rounded-[3px] border border-gray-200 px-10 py-6 sm:w-11/12 sm:shadow">
                        <h2 className="font-bold text-gray-700">
                          {customer?.firstName
                            ? `Hi ${customer.firstName}, you have no orders yet!`
                            : 'You currently have no orders in your account'}
                        </h2>
                        <p className="mt-2 text-sm text-gray-700">
                          Once you've placed an order, you'll find all the
                          details here. If you've already placed one and don't
                          see it, try logging out and logging back in—it might
                          be linked to a different session.
                        </p>
                        <Link href={'/'} passHref>
                          <CtaButton
                            variant="dark"
                            className="mt-3 inline-block w-full xxs:w-3/5 sm:w-2/5"
                          >{t`Let's go shopping`}</CtaButton>
                        </Link>
                      </div>
                      <div className="my-3 w-full rounded-[3px] border border-gray-200 px-10 py-6 sm:w-11/12 sm:shadow">
                        <p className="mt-2 text-sm text-gray-700">
                          If you believe you’ve placed an order, you might have
                          another account with a different email address. Please
                          try logging in with that email to view your order.
                        </p>
                        <p className="mt-2 text-sm text-gray-700">
                          If you need any help or aren’t sure about your other
                          login details, feel free to contact our support team
                          by using the form below. We’d be happy to assist!
                        </p>
                      </div>
                      <div className="w-full rounded-[3px] border border-gray-200 sm:w-11/12 sm:shadow">
                        <ContactUsForm isLoggedIn={!!customer} />
                      </div>
                    </div>
                  ) : (
                    <>
                      {orderData?.map((order, idx) => (
                        <OrderUnit key={idx} order={order} index={idx} />
                      ))}
                      <div
                        ref={endOfContainerRef} // End marker for IntersectionObserver
                        // eslint-disable-next-line react/forbid-dom-props
                        style={{ height: '1px' }}
                      />
                    </>
                  )}
                </>
              )}
              {isFetching && shouldRenderPage && <Loading />}
            </div>
          </div>
        </div>
        <AccountRecommendations />
      </div>
    </>
  );
}
